/**
 * Consent resolution (PRD §2.1). No video ad is requested until the consent state
 * is known, and every request carries it explicitly.
 */

/**
 * @typedef {object} ConsentSignals
 * @property {0|1|null} gdpr null when unknown (CMP silent)
 * @property {string} gdprConsent TC string
 * @property {string} addtlConsent Google Additional Consent string
 * @property {string} gpp GPP string
 * @property {string} gppSid applicable section ids, comma separated
 * @property {0|1} npa non-personalised ads
 * @property {0|1} ltd limited ads
 * @property {0|1} rdp restricted data processing
 * @property {0|1} tfcd child-directed treatment
 * @property {boolean} analyticsAllowed analytics_storage may be used (§15.4)
 * @property {boolean} tcfSilent
 * @property {boolean} gppSilent
 */

/**
 * Wait for the TCF v2.2 CMP to report tcloaded or useractioncomplete.
 * @param {Window} win
 * @returns {Promise<object>} tcData
 */
function waitForTcf(win) {
  return new Promise((resolve) => {
    if (typeof win.__tcfapi !== 'function') return;
    try {
      win.__tcfapi('addEventListener', 2, (tcData, success) => {
        if (!success || !tcData) return;
        if (tcData.gdprApplies === false || tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
          try {
            if (tcData.listenerId !== undefined) win.__tcfapi('removeEventListener', 2, () => {}, tcData.listenerId);
          } catch (error) {
            // Ignore CMPs without removeEventListener.
          }
          resolve(tcData);
        }
      });
    } catch (error) {
      // A broken CMP counts as silent.
    }
  });
}

/**
 * Wait for the GPP CMP to report signalStatus "ready".
 * @param {Window} win
 * @returns {Promise<object>} pingData
 */
function waitForGpp(win) {
  return new Promise((resolve) => {
    if (typeof win.__gpp !== 'function') return;
    const accept = (data) => {
      const ping = data && (data.pingData || data);
      if (ping && ping.signalStatus === 'ready') resolve(ping);
    };
    try {
      win.__gpp('addEventListener', (event) => accept(event));
      win.__gpp('ping', (ping) => accept(ping));
    } catch (error) {
      // A broken CMP counts as silent.
    }
  });
}

/**
 * True when any US section in GPP parsedSections records an opt-out of sale, sharing or targeting.
 * @param {object} ping GPP pingData
 * @returns {boolean}
 */
export function gppHasOptOut(ping) {
  const sections = (ping && ping.parsedSections) || {};
  return Object.keys(sections).some((name) => {
    const entries = [].concat(sections[name]);
    return entries.some(
      (fields) => fields && (fields.SaleOptOut === 1 || fields.SharingOptOut === 1 || fields.TargetedAdvertisingOptOut === 1),
    );
  });
}

/**
 * Turn CMP answers into the explicit signals of the §2.1 table.
 * @param {object} input
 * @param {object|null} input.tcData null when the TCF CMP stayed silent
 * @param {object|null} input.gppData null when the GPP CMP stayed silent
 * @param {boolean} input.childDirected config privacy.childDirected
 * @returns {ConsentSignals}
 */
export function signalsFromCmp({ tcData, gppData, childDirected }) {
  const signals = {
    gdpr: null,
    gdprConsent: '',
    addtlConsent: '',
    gpp: '',
    gppSid: '',
    npa: 0,
    ltd: 0,
    rdp: 0,
    tfcd: 0,
    analyticsAllowed: true,
    tcfSilent: !tcData,
    gppSilent: !gppData,
  };
  if (tcData) {
    signals.gdpr = tcData.gdprApplies ? 1 : 0;
    if (tcData.gdprApplies) {
      signals.gdprConsent = tcData.tcString || '';
      signals.addtlConsent = tcData.addtlConsent || '';
      const consents = (tcData.purpose && tcData.purpose.consents) || {};
      // Purpose 1 = store/access information on a device.
      if (!consents[1]) {
        signals.ltd = 1; // §2.1: no consent to cookies/storage -> limited ads
        signals.npa = 1;
        signals.analyticsAllowed = false;
      }
      // Purposes 3 and 4 = personalised ads profile and selection.
      if (!consents[3] || !consents[4]) signals.npa = 1;
    }
  }
  if (gppData) {
    signals.gpp = gppData.gppString || '';
    const sids = gppData.applicableSections || [];
    signals.gppSid = sids.filter((sid) => sid !== -1 && sid !== 0).join(',');
    if (gppHasOptOut(gppData)) signals.rdp = 1; // §2.1: US state opt-out
  }
  if (!tcData && !gppData) {
    // Assumption A10 (Q10): no CMP answer at all after 2 s -> strictest: ltd=1 + npa=1.
    signals.ltd = 1;
    signals.npa = 1;
  } else if (!tcData) {
    // §2.1 / §3.1: TCF CMP silent after 2 s -> treat as a consent region with no answer.
    signals.ltd = 1;
    signals.npa = 1;
  }
  if (childDirected) {
    signals.tfcd = 1; // §2.1: child-directed page
    signals.npa = 1; // and no personalisation
  }
  return signals;
}

/**
 * Resolve consent before any IMA, Prebid or AdSense load (§2.1, PRV-1).
 * Waits for __tcfapi and __gpp with one shared timeout (2 s).
 * @param {object} options
 * @param {Window} options.win
 * @param {boolean} options.requireCmp
 * @param {number} options.timeoutMs
 * @param {boolean} options.childDirected
 * @param {Function} options.setTimeoutFn
 * @returns {Promise<ConsentSignals>}
 */
export function resolveConsent({ win, requireCmp, timeoutMs, childDirected, setTimeoutFn }) {
  if (!requireCmp) {
    return Promise.resolve(signalsFromCmp({ tcData: { gdprApplies: false }, gppData: null, childDirected }));
  }
  let tcData = null;
  let gppData = null;
  const tcfPromise = waitForTcf(win).then((data) => (tcData = data));
  const gppPromise = waitForGpp(win).then((data) => (gppData = data));
  const hasTcf = typeof win.__tcfapi === 'function';
  const hasGpp = typeof win.__gpp === 'function';
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve(signalsFromCmp({ tcData, gppData, childDirected }));
    };
    setTimeoutFn(finish, timeoutMs);
    // Resolve early once every CMP on the page has answered. Without __tcfapi we wait
    // the full timeout, since the CMP may still be loading (silent = strictest signals).
    if (hasTcf) Promise.all(hasGpp ? [tcfPromise, gppPromise] : [tcfPromise]).then(finish);
  });
}
